import React, { useContext, useEffect, useRef, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import BankAccountForm from './BankAccountForm';
import CardForm from './CardForm';
import { NotificationType, SERVER_URL } from "../../constants/global";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useQueryUniqueUsers } from "../../queries/pharmacy/hooks/useQueryUniqueUsers";
import { UserContext } from "../../hooks/userContext";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import { useAuth } from "../../hooks/useAuth";
import Link from "@mui/material/Link";
import { useSearchParams } from "react-router-dom";
import { ToasterContext } from "../../components/shared/Toaster/ToasterContext";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PayoutMethod from "./PayoutMethod";
import { AchBrandMark, paymentStatusBadge } from "./MethodAccountCard";
import { CardBrandIcon } from "../Checkout/CardBrandIcons";

const stripePublicKey: string = process.env.REACT_APP_STRIPE_PUBLIC_KEY || '';

const stripePromise = loadStripe(stripePublicKey);

type PaymentMethodType = "us_bank_account" | "card";
type PaymentView = "ach" | "card";

interface SetupIntentResponse {
  client_secret: string;
  BankAccount: { id: number };
}

export const BankAccount: React.FunctionComponent = () => {
  const { id: userId }: any = useContext(UserContext);
  const { addToast } = useContext(ToasterContext);
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('required');
  const [paymentView, setPaymentView] = useState<PaymentView>('ach');
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [selectedType, setSelectedType] = useState<PaymentMethodType>('us_bank_account');
  const [setupIntentData, setSetupIntentData] = useState<SetupIntentResponse | null>(null);
  const [setupIntentLoading, setSetupIntentLoading] = useState<boolean>(false);
  const [isRefreshingPaymentMethod, setIsRefreshingPaymentMethod] = useState(false);
  const [removingAccountId, setRemovingAccountId] = useState<string | null>(null);
  const [pendingRemove, setPendingRemove] = useState<{
    accountId: string;
    label: string;
  } | null>(null);

  const {
    data: pharmacyUsersData,
    loading: pharmacyUsersLoading,
    refetch: refetchPharmacyUser,
  } = useQueryUniqueUsers({
    variables: { where: { id: +userId } },
    // Always load fresh pharmacy flags (e.g. cardPaymentEnabled) when opening this page.
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const handlePaymentMethodSaved = async () => {
    setIsRefreshingPaymentMethod(true);
    setIsEdit(false);
    try {
      await refetchPharmacyUser();
    } finally {
      setIsRefreshingPaymentMethod(false);
    }
  };

  const pharmacy = pharmacyUsersData?.findUniqueUser?.PharmacyUsers?.[0]?.Pharmacy;
  const cardPaymentEnabled = pharmacy?.cardPaymentEnabled ?? false;
  const bankAccounts = pharmacy?.BankAccount || [];

  const payoutAccount = bankAccounts.find(
    (item: any) => item.object === 'bank_account'
  );
  const achAccount = bankAccounts.find(
    (item: any) => item.object === 'payment_method' && item.accountId
  );
  const cardAccount = bankAccounts.find(
    (item: any) => item.object === 'card' && item.accountId
  );

  const hasAnySavedMethod = Boolean(achAccount || cardAccount);
  // Must keep at least one ACH or card — Remove is only active when both are on file.
  const canRemoveASavedMethod = Boolean(achAccount && cardAccount);
  const lastMethodRemoveReason =
    'You must keep at least one payment method on file.';
  const showForm = !hasAnySavedMethod || isEdit;

  const startEdit = (type: PaymentMethodType) => {
    setSelectedType(type);
    setPaymentView(type === 'card' ? 'card' : 'ach');
    setIsEdit(true);
  };

  const handleConfirmRemove = async () => {
    if (!pendingRemove || !token || removingAccountId) {
      return;
    }
    const { accountId, label } = pendingRemove;
    setRemovingAccountId(accountId);
    try {
      const response = await fetch(
        `${SERVER_URL}/stripe/payment-method/${accountId}?keepOnStripe=true`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message =
          typeof data?.message === 'string'
            ? data.message
            : Array.isArray(data?.message)
              ? data.message.join(', ')
              : `Could not remove ${label}.`;
        throw new Error(message);
      }
      setPendingRemove(null);
      setIsRefreshingPaymentMethod(true);
      try {
        await refetchPharmacyUser();
      } finally {
        setIsRefreshingPaymentMethod(false);
      }
      addToast({
        type: NotificationType.Success,
        title: `${label} removed.`,
      });
    } catch (err: any) {
      addToast({
        type: NotificationType.Error,
        title: err?.message || `Could not remove ${label}.`,
      });
    } finally {
      setRemovingAccountId(null);
    }
  };

  const didSetInitialPaymentView = useRef(false);

  // Force ACH if card payment is disabled for this pharmacy.
  useEffect(() => {
    if (!cardPaymentEnabled && (selectedType === 'card' || paymentView === 'card')) {
      setSelectedType('us_bank_account');
      setPaymentView('ach');
    }
  }, [cardPaymentEnabled, selectedType, paymentView]);

  // Default the dropdown once, after accounts load. Do not keep correcting it,
  // or choosing "Add ACH" snaps back to the saved card.
  useEffect(() => {
    if (didSetInitialPaymentView.current || !pharmacy) {
      return;
    }
    didSetInitialPaymentView.current = true;
    if (!achAccount && cardAccount) {
      setPaymentView('card');
      setSelectedType('card');
    }
  }, [pharmacy, achAccount, cardAccount]);

  // Fetch a fresh Setup Intent whenever the selected type changes while the form is shown.
  // Stripe requires <Elements> to be remounted (via key={clientSecret}) when clientSecret changes.
  useEffect(() => {
    if (!showForm || !token) return;
    let cancelled = false;

    const fetchSetupIntent = async () => {
      setSetupIntentLoading(true);
      try {
        const response = await fetch(
          `${SERVER_URL}/stripe/setup-intent?type=${selectedType}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await response.json();
        if (!cancelled) setSetupIntentData(data);
      } catch {
        if (!cancelled) {
          addToast({
            type: NotificationType.Error,
            title: 'Failed to initialize payment setup. Please try again.',
          });
        }
      } finally {
        if (!cancelled) setSetupIntentLoading(false);
      }
    };

    fetchSetupIntent();
    return () => {
      cancelled = true;
    };
  }, [selectedType, showForm, token, addToast]);

  useEffect(() => {
    if (tab === 'true') {
      addToast({
        type: NotificationType.Error,
        title: 'You are required to add your bank information before you can make a purchase.',
      });
    }
  }, [tab, addToast]);

  if (pharmacyUsersLoading && !pharmacy) {
    return (
      <Paper
        sx={{
          p: 6,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          minHeight: 240,
        }}
      >
        <CircularProgress color="inherit" />
        <Typography color="text.secondary">Loading…</Typography>
      </Paper>
    );
  }

  if (!pharmacy) {
    return (
      <Paper sx={{ p: 4 }}>
        <Typography color="text.secondary">Unable to load bank information.</Typography>
      </Paper>
    );
  }

  const sameTypeOldAccountId =
    selectedType === 'card' ? cardAccount?.accountId : achAccount?.accountId;

  const paymentMethodForm = (setupIntentLoading || !setupIntentData?.client_secret) ? (
    <Grid sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
      <CircularProgress color="inherit" />
    </Grid>
  ) : (
    <Elements
      stripe={stripePromise}
      key={setupIntentData.client_secret}
    >
      {selectedType === 'us_bank_account' ? (
        <BankAccountForm
          userId={+userId}
          pharmacy={pharmacyUsersData.findUniqueUser.PharmacyUsers[0]}
          clientSecret={setupIntentData.client_secret}
          bankAccountId={setupIntentData.BankAccount.id}
          pharmacyUsersLoading={pharmacyUsersLoading}
          setIsEdit={setIsEdit}
          onCancel={() => {
            setIsEdit(false);
            if (!achAccount && cardAccount) {
              setPaymentView('card');
              setSelectedType('card');
            }
          }}
          onPaymentMethodSaved={handlePaymentMethodSaved}
          oldAccountId={sameTypeOldAccountId}
        />
      ) : (
        <CardForm
          userId={+userId}
          pharmacy={pharmacyUsersData.findUniqueUser.PharmacyUsers[0]}
          clientSecret={setupIntentData.client_secret}
          bankAccountId={setupIntentData.BankAccount.id}
          pharmacyUsersLoading={pharmacyUsersLoading}
          setIsEdit={setIsEdit}
          onCancel={() => {
            setIsEdit(false);
            if (!achAccount && cardAccount) {
              setPaymentView('card');
              setSelectedType('card');
            }
          }}
          onPaymentMethodSaved={handlePaymentMethodSaved}
          oldAccountId={sameTypeOldAccountId}
        />
      )}
    </Elements>
  );

  const achLabel = achAccount
    ? `ACH · ${achAccount.bankName || 'Bank'} · •••• ${achAccount.last4 || ''}`
    : 'Add ACH bank account';
  const cardLabel = cardAccount
    ? `${cardAccount.bankName || 'Card'} · •••• ${cardAccount.last4 || ''}`
    : 'Add credit or debit card';

  const handlePaymentSelect = (value: PaymentView) => {
    setPaymentView(value);
    const type: PaymentMethodType = value === 'card' ? 'card' : 'us_bank_account';
    setSelectedType(type);
    const exists = value === 'card' ? Boolean(cardAccount) : Boolean(achAccount);
    setIsEdit(!exists);
  };

  const selectedSaved = paymentView === 'card' ? cardAccount : achAccount;
  const selectedBadge = paymentView === 'card'
    ? { label: 'Verified', tone: 'success' as const }
    : paymentStatusBadge(achAccount?.status);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%', maxWidth: 880, mx: 'auto' }}>
      <Box>
        <Typography component="h1" variant="h5" sx={{ fontWeight: 700, letterSpacing: -0.2 }}>
          Payment Methods & Payout Methods
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Open a section to view or update the saved method.
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box
            component="button"
            type="button"
            onClick={() => setPaymentOpen((open) => !open)}
            aria-expanded={paymentOpen}
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              p: 3,
              m: 0,
              border: 'none',
              bgcolor: 'transparent',
              textAlign: 'left',
              cursor: 'pointer',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <ArrowDownwardIcon sx={{ color: 'text.secondary' }} />
              <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Payment Method</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Pay-in methods used for buy orders.
              </Typography>
              </Box>
            </Box>
            <ExpandMoreIcon
              sx={{
                color: 'text.secondary',
                transform: paymentOpen ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s',
              }}
            />
          </Box>
          <Collapse in={paymentOpen}>
            <Box sx={{ px: 3, pb: 3 }}>
          <FormControl fullWidth>
            <InputLabel id="payment-method-select">Method</InputLabel>
            <Select
              labelId="payment-method-select"
              label="Method"
              value={paymentView}
              renderValue={(value) => (value === 'card' ? cardLabel : achLabel)}
              onChange={(event) => handlePaymentSelect(event.target.value as PaymentView)}
            >
              <MenuItem value="ach">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <AchBrandMark />
                  {achLabel}
                </Box>
              </MenuItem>
              {cardPaymentEnabled && (
                <MenuItem value="card">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <CardBrandIcon brand={cardAccount?.bankName} compact />
                    {cardLabel}
                  </Box>
                </MenuItem>
              )}
            </Select>
          </FormControl>

          {isRefreshingPaymentMethod ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress color="inherit" />
            </Box>
          ) : showForm && ((paymentView === 'card') === (selectedType === 'card')) ? (
            <Box sx={{ mt: 3 }}>{paymentMethodForm}</Box>
          ) : selectedSaved ? (
            <Box sx={{ mt: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
                <Box
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: 10,
                    fontSize: 12,
                    fontWeight: 700,
                    color: selectedBadge.tone === 'warning' ? '#E65100' : '#1B5E20',
                    bgcolor: selectedBadge.tone === 'warning' ? '#FFF3E0' : '#E8F5E9',
                  }}
                >
                  {selectedBadge.label}
                </Box>
              </Box>
              {(paymentView === 'card'
                ? [
                    { label: 'Brand', value: cardAccount?.bankName },
                    { label: 'Name', value: cardAccount?.accountHolderName },
                    { label: 'Card #', value: cardAccount?.last4 ? `•••• ${cardAccount.last4}` : null },
                  ]
                : [
                    { label: 'Bank', value: achAccount?.bankName },
                    { label: 'Name', value: achAccount?.accountHolderName },
                    { label: 'Account #', value: achAccount?.last4 ? `•••• ${achAccount.last4}` : null },
                    { label: 'Routing #', value: achAccount?.routingNumber },
                  ]
              )
                .filter((row) => row.value)
                .map((row) => (
                  <Box
                    key={row.label}
                    sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75 }}
                  >
                    <Typography variant="body2" color="text.secondary">{row.label}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.value}</Typography>
                  </Box>
                ))}
              {paymentView === 'ach' && achAccount?.status === 'PENDING' && achAccount?.hostedVerificationUrl && (
                <Link color="primary" href={achAccount.hostedVerificationUrl} target="_blank">
                  Verify Bank Account
                </Link>
              )}
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<EditOutlinedIcon />}
                  onClick={() => startEdit(paymentView === 'card' ? 'card' : 'us_bank_account')}
                  sx={{ textTransform: 'none', fontWeight: 700 }}
                >
                  Edit
                </Button>
                <Button
                  color="error"
                  variant="outlined"
                  disabled={!canRemoveASavedMethod || removingAccountId === selectedSaved.accountId}
                  onClick={() =>
                    setPendingRemove({
                      accountId: selectedSaved.accountId,
                      label: paymentView === 'card' ? 'Card' : 'Bank account',
                    })
                  }
                  sx={{ textTransform: 'none' }}
                >
                  {removingAccountId === selectedSaved.accountId ? 'Deleting…' : 'Delete'}
                </Button>
              </Box>
            </Box>
          ) : null}

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2.5, lineHeight: 1.5 }}>
            These methods pay for buy orders. Choose which one to use at checkout. A method can be removed only when both are on file.
          </Typography>
            </Box>
          </Collapse>
        </Paper>

        <PayoutMethod
          bankAccount={payoutAccount}
          pharmacyId={pharmacy.id}
          userId={+userId}
        />
      </Box>

      <Dialog
        open={Boolean(pendingRemove)}
        onClose={() => {
          if (!removingAccountId) {
            setPendingRemove(null);
          }
        }}
      >
        <DialogTitle>Remove payment method?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingRemove
              ? `Remove your saved ${pendingRemove.label.toLowerCase()}? Your other payment method will stay on file.`
              : null}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={Boolean(removingAccountId)}
            onClick={() => setPendingRemove(null)}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={Boolean(removingAccountId)}
            onClick={() => void handleConfirmRemove()}
          >
            {removingAccountId ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BankAccount;
